import { FC, useCallback, useEffect, useState } from 'react';
import { Modal } from 'antd';
import { TextInput } from '../../ui/TextInput';
import { useStore } from '@nanostores/react';
import { v4 as uuidv4 } from 'uuid';
import {
  $playFolderModalIsOpen,
  $playFolders,
  $playFoldersIsLoading,
  getFolders,
  savePlayFolder,
} from '../../store/playFolders';
import { Play, PlayFolder, Syncable } from '../../types';
import { Button } from '../../ui/Button';
import {
  useDeletePlayFolderMutation,
} from '../../mutations';
import { lighterBlueBackground, red } from '../colors';

export const CreateFolderModal: FC<{ plays: Array<Play>}> = ({ plays }) => {

  const modalStore = useStore($playFolderModalIsOpen)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  useEffect(() => {
    setIsOpen(modalStore.isOpen)
  }, [modalStore]);

  if (!isOpen) return null
  return <CreateFolderModalContent plays={plays} />
}

const CreateFolderModalContent: FC<{ plays: Array<Play> }> = ({ plays }) => {
  const modalStore = useStore($playFolderModalIsOpen)
  const folders = useStore($playFolders)
  const isFoldersLoading = useStore($playFoldersIsLoading)
  const [selectedFolder, setSelectedFolder] = useState<Syncable<PlayFolder> | null>(null)
  const [folderName, setFolderName] = useState<string>('');
  const [saveIsLoading, setSaveIsLoading] = useState<boolean>(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState<boolean>(true)

  useEffect(() => {
    const loadFolders = async () => {
      await getFolders()
    }
    void loadFolders()
  }, []);

  useEffect(() => {
    setSelectedFolder(modalStore.folder ? modalStore.folder : null)
    setFolderName(modalStore.folder ? modalStore.folder.name : '')
  }, [modalStore]);

  const { mutateAsync: asyncDelete, isLoading: deleteMutationLoading, isError: deleteError } = useDeletePlayFolderMutation()

  const onInputChange = useCallback((value: string) => {
    setFolderName(value)
    setValidationError(null)
  }, [setFolderName, setValidationError])

  const onDeletePlayFolder = useCallback(async () => {
    setValidationError(null)
    if (!selectedFolder || selectedFolder.id === undefined) return
    const found = plays?.find(p => p.folderId === selectedFolder?.id)
    if ((found && !selectedFolder) || (found && selectedFolder && found.id !== selectedFolder.id)) {
      setValidationError('A folder must be empty before it can be deleted.')
      return
    }
    const res = await asyncDelete(selectedFolder)
    if (res?.id === selectedFolder.id) {
      $playFolders.set($playFolders.get().filter((f) => f.id !== selectedFolder.id))
      void getFolders()
    }
    $playFolderModalIsOpen.set({ isOpen: false, folder: undefined })
  }, [plays, setValidationError, asyncDelete, selectedFolder])

  const onSaveFolder = useCallback(async () => {
    setValidationError(null)
    if (folderName.trim().length === 0) {
      setValidationError('Folder name cannot be empty.')
      return
    }
    const found = folders?.find(f => f.name.toLowerCase() === folderName.trim().toLowerCase())
    if ((found && !selectedFolder) || (found && selectedFolder && found.id !== selectedFolder.id)) {
      setValidationError('A folder with this name already exists.')
      return
    }
    setSaveIsLoading(true)
    try {
      const res = await savePlayFolder({
        id: selectedFolder ? selectedFolder.id : uuidv4(),
        isDraft: selectedFolder === null || selectedFolder.isDraft,
        synced: false,
        name: folderName.trim(),
        description: ''
      })
      setFolderName('')
      setSelectedFolder(null)
      void getFolders()
      onClose()
    } catch (e) {

    }
    setSaveIsLoading(false)
  }, [folders, folderName])

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  return (
    <Modal
      open={isOpen}
      centered={true}
      closable={false}
      footer={null}
      afterClose={() => {
        $playFolderModalIsOpen.set({ isOpen: false, folder: undefined })
      }}
      styles={{
        header: {
          background: lighterBlueBackground,
          fontSize: '30px !important',
          color: '#fff'
        },
        content: {
          background: lighterBlueBackground,
          color: '#fff'
        }
      }}
    >
      <div>
        <div>
          <h2 style={{ color: '#fff', textAlign: 'center', marginBottom: '20px' }}>{selectedFolder ? 'Edit Folder' : 'Create New Folder'}</h2>
        </div>
        <TextInput autocomplete={'off'} value={folderName} onChange={onInputChange} placeholder={'Folder name'} />
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: '24px', marginTop: '22px' }}>
          <Button style={{ width: '140px'}} large={true} disabled={isFoldersLoading || saveIsLoading} onClick={() => void onSaveFolder()}>
            Save
          </Button>
          <Button style={{ width: '140px'}} secondary={true} large={true} onClick={() => onClose()}>Cancel</Button>
        </div>
        {selectedFolder && (
          <div style={{ marginTop: '12px', textAlign: 'center' }}>
            <Button style={{ width: 'auto', background: red }} onClick={() => onDeletePlayFolder()}>DELETE FOLDER</Button>
          </div>
        )}
        {/*{isError && (*/}
        {/*  <div>*/}
        {/*    <p style={{ color: 'red', marginTop: '12px', textAlign: 'center' }}>*/}
        {/*      An error occurred while saving the folder. Please try again.*/}
        {/*    </p>*/}
        {/*  </div>*/}
        {/*)}*/}
        {validationError && (
          <div>
            <p style={{ color: 'red', marginTop: '12px', textAlign: 'center' }}>
              {validationError}
            </p>
          </div>
        )}
      </div>
    </Modal>
  )
}